/**
 * TotalEnergies fuel invoices — Appwrite backend (collection: TotalEnergiesInvoicesSGM)
 *
 * Setup:  npm run setup:total-energies-invoices
 * Used by: Fuel Report → TotalEnergies Invoices tab
 */

import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_TOTAL_ENERGIES_INVOICES
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const LIST_PAGE_SIZE = 100;
const LIST_MAX_PAGES = 50;

const COLLECTION_SETUP_HINT =
  'Create the TotalEnergies Invoices collection: run `npm run setup:total-energies-invoices` (set APPWRITE_API_KEY in .env). Then wait until attributes are "available" in Appwrite Console.';

const toNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export function invoiceDateToYm(invoiceDate) {
  const s = String(invoiceDate ?? '').trim();
  if (!s) return '';
  const match = s.match(/^([A-Za-z]+)\s*[-–]?\s*(\d{4})$/);
  if (!match) return '';
  const monthName = match[1];
  const year = parseInt(match[2], 10);
  const d = new Date(`${monthName} 1, ${year}`);
  if (Number.isNaN(d.getTime())) return '';
  return `${year}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function formatInvoiceDateFromYm(ym) {
  if (!ym || typeof ym !== 'string' || ym.length < 7) return '';
  const [y, m] = ym.split('-');
  const year = parseInt(y, 10);
  const month = parseInt(m, 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return '';
  const monthName = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' });
  return `${monthName} - ${year}`;
}

export const calcInvoiceTotal = (fuelAmount, managementFee, vat) =>
  toNumber(fuelAmount) + toNumber(managementFee) + toNumber(vat);

export const formatTotalEnergiesInvoice = (doc) => ({
  id: doc.$id ?? doc.id,
  documentId: doc.$id ?? doc.id,
  invoiceNo: doc.invoiceNo || '',
  invoiceDate: doc.invoiceDate || '',
  invoiceMonthYm: doc.invoiceMonthYm || invoiceDateToYm(doc.invoiceDate),
  project: doc.project || '',
  fuelAmount: toNumber(doc.fuelAmount, 0),
  managementFee: toNumber(doc.managementFee, 0),
  vat: toNumber(doc.vat, 0),
  totalAmount: toNumber(doc.totalAmount, calcInvoiceTotal(doc.fuelAmount, doc.managementFee, doc.vat)),
  po: doc.po || '',
  paymentStatus: doc.paymentStatus || 'Pending',
  datePaid: doc.datePaid || '-',
  comment: doc.comment || '',
  sortOrder: Math.round(toNumber(doc.sortOrder, 0))
});

export const emptyTotalEnergiesInvoiceFields = () => ({
  invoiceNo: '',
  invoiceDate: '',
  invoiceMonthYm: '',
  project: '',
  fuelAmount: 0,
  managementFee: 0,
  vat: 0,
  totalAmount: 0,
  po: '',
  paymentStatus: 'Pending',
  datePaid: '-',
  comment: '',
  sortOrder: 0
});

export const toTotalEnergiesInvoicePayload = (fields) => {
  const fuelAmount = toNumber(fields.fuelAmount, 0);
  const managementFee = toNumber(fields.managementFee, 0);
  const vat = toNumber(fields.vat, 0);
  const invoiceDate = String(fields.invoiceDate || '').trim();
  const invoiceMonthYm =
    String(fields.invoiceMonthYm || '').trim() || invoiceDateToYm(invoiceDate);

  return {
    invoiceNo: String(fields.invoiceNo || '').trim(),
    invoiceDate,
    invoiceMonthYm,
    project: String(fields.project || '').trim(),
    fuelAmount,
    managementFee,
    vat,
    totalAmount: calcInvoiceTotal(fuelAmount, managementFee, vat),
    po: String(fields.po || '').trim(),
    paymentStatus: String(fields.paymentStatus || 'Pending').trim() || 'Pending',
    datePaid: String(fields.datePaid || '-').trim() || '-',
    comment: String(fields.comment || '').trim(),
    sortOrder: Math.round(toNumber(fields.sortOrder, 0))
  };
};

function isCollectionMissing(error) {
  if (!error) return false;
  if (error.code === 404) return true;
  const msg = String(error.message || '').toLowerCase();
  if (msg.includes('could not be found')) return true;
  if (msg.includes('collection') && msg.includes('not found')) return true;
  if (msg.includes('totalenergiesinvoicessgm')) return true;
  return false;
}

export const getTotalEnergiesInvoiceErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  if (error.code === 'TOTAL_ENERGIES_INVOICES_COLLECTION_MISSING') {
    return COLLECTION_SETUP_HINT;
  }
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code === 404) return COLLECTION_SETUP_HINT;
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

const listAllDocuments = async () => {
  const all = [];
  let cursor = null;

  for (let page = 0; page < LIST_MAX_PAGES; page += 1) {
    const queries = [Query.limit(LIST_PAGE_SIZE), Query.orderAsc('sortOrder')];
    if (cursor) queries.push(Query.cursorAfter(cursor));

    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_TOTAL_ENERGIES_INVOICES,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatTotalEnergiesInvoice));
    if (docs.length < LIST_PAGE_SIZE) break;
    cursor = docs[docs.length - 1].$id;
  }

  return all;
};

export const fetchAllTotalEnergiesInvoices = async () => {
  try {
    return await listAllDocuments();
  } catch (error) {
    console.error('Error fetching TotalEnergies invoices:', error);
    if (isCollectionMissing(error)) {
      const err = new Error(COLLECTION_SETUP_HINT);
      err.code = 'TOTAL_ENERGIES_INVOICES_COLLECTION_MISSING';
      throw err;
    }
    throw error;
  }
};

export const createTotalEnergiesInvoice = async (fields) => {
  const payload = toTotalEnergiesInvoicePayload(fields);
  try {
    const created = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID_TOTAL_ENERGIES_INVOICES,
      ID.unique(),
      payload
    );
    return formatTotalEnergiesInvoice(created);
  } catch (error) {
    if (isCollectionMissing(error)) {
      const err = new Error(COLLECTION_SETUP_HINT);
      err.code = 'TOTAL_ENERGIES_INVOICES_COLLECTION_MISSING';
      throw err;
    }
    throw error;
  }
};

export const updateTotalEnergiesInvoice = async (id, fields) => {
  if (!id) throw new Error('Invoice id is required.');
  const payload = toTotalEnergiesInvoicePayload(fields);
  try {
    const updated = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_TOTAL_ENERGIES_INVOICES,
      id,
      payload
    );
    return formatTotalEnergiesInvoice(updated);
  } catch (error) {
    if (isCollectionMissing(error)) {
      const err = new Error(COLLECTION_SETUP_HINT);
      err.code = 'TOTAL_ENERGIES_INVOICES_COLLECTION_MISSING';
      throw err;
    }
    throw error;
  }
};

export const deleteTotalEnergiesInvoice = async (id) => {
  if (!id) throw new Error('Invoice id is required.');
  try {
    await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_TOTAL_ENERGIES_INVOICES, id);
    return true;
  } catch (error) {
    if (isCollectionMissing(error)) {
      const err = new Error(COLLECTION_SETUP_HINT);
      err.code = 'TOTAL_ENERGIES_INVOICES_COLLECTION_MISSING';
      throw err;
    }
    throw error;
  }
};
